import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { colors } from "@/lib/theme";
import { Spinner, GoogleIcon } from "@/components/peeps/ui";

export function GoogleAccounts() {
  const [accounts, setAccounts] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setAccounts(await api.googleAccounts()); }
    catch { setAccounts([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Surface the result of the connect handshake when Google redirects back.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("gaccount") === "connected") { toast("Google account connected"); load(); }
    else if (p.get("gaccount") === "primary") toast("That's already your sign-in account");
    else if (p.get("gaccount_error")) toast("Couldn't connect that account");
    if (p.get("gaccount") || p.get("gaccount_error")) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [load]);

  const connect = async () => {
    setBusy(true);
    try {
      const { authorization_url } = await api.connectGoogleAccount(window.location.origin + "/profile");
      window.location.href = authorization_url;
    } catch (e) {
      setBusy(false);
      toast(e.message || "Google isn't configured");
    }
  };

  const disconnect = async (a) => {
    setAccounts((prev) => prev.filter((x) => x.id !== a.id));
    try { await api.disconnectGoogleAccount(a.id); toast(`Disconnected ${a.name || a.email}`); }
    catch (e) { toast(e.message || "Couldn't disconnect"); load(); }
  };

  const card = { background: colors.surfaceSecondary, border: `1px solid ${colors.border}`, borderRadius: 16 };

  return (
    <div data-testid="google-accounts-section">
      <p className="font-display font-medium text-[11.5px] uppercase tracking-wide mb-2 ml-1" style={{ color: colors.muted }}>
        Connected calendars
      </p>
      <div style={card} className="overflow-hidden">
        {accounts === null ? (
          <div className="p-4"><Spinner /></div>
        ) : (
          <>
            {accounts.length === 0 && (
              <div className="p-4">
                <p className="font-display text-[13px]" style={{ color: colors.onSurfaceTertiary }}>
                  No Google account connected yet. Connect one to share your availability and see your calendars in the app.
                </p>
              </div>
            )}
            {accounts.map((a) => (
              <div key={a.id} data-testid={`gaccount-${a.id}`} className="flex items-center gap-3 p-4" style={{ borderBottom: `1px solid ${colors.border}` }}>
                <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: colors.surfaceTertiary }}>
                  <Mail size={16} color={colors.brandSecondary} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display font-semibold text-[14px] truncate" style={{ color: colors.onSurface }}>{a.name || a.email}</p>
                  <p className="font-display text-[12px] truncate" style={{ color: colors.faint }}>{a.email}{a.is_primary ? " · sign-in account" : ""}</p>
                </div>
                {a.is_primary ? (
                  <span className="flex items-center gap-1 shrink-0" style={{ color: colors.faint }}>
                    <ShieldCheck size={13} /> <span className="font-display text-[11px]">Locked</span>
                  </span>
                ) : (
                  <button data-testid={`gaccount-disconnect-${a.id}`} onClick={() => disconnect(a)} className="p-2 rounded-lg shrink-0" style={{ background: colors.surfaceTertiary }} title="Disconnect">
                    <Trash2 size={15} color={colors.error} />
                  </button>
                )}
              </div>
            ))}
            <button
              data-testid="connect-google-account"
              onClick={connect}
              disabled={busy}
              className="flex items-center justify-center gap-2.5 w-full p-4 transition-transform active:scale-[0.99]"
            >
              {busy ? <Spinner /> : (
                <>
                  <GoogleIcon size={16} />
                  <span className="font-display font-semibold text-[13.5px]" style={{ color: colors.brandSecondary }}>Connect another Google account</span>
                  <Plus size={15} color={colors.brandSecondary} />
                </>
              )}
            </button>
          </>
        )}
      </div>
      <p className="font-display text-[12px] mt-2 px-1" style={{ color: colors.faint }}>
        Add your work or personal account — all their calendars show up together, labelled by account.
      </p>
    </div>
  );
}
